/*
  kennix-carousel.js
  Shared carousel controller for Shop by Category and Best Sellers.

  Features:
  - Dynamic left/right arrow visibility based on scroll position
  - Hides both arrows when all content fits in the viewport
  - Scrolls by one visible container width per click
  - Desktop mouse-drag support
  - Touch/swipe (native, no JS needed — handled by CSS scroll)
  - Trackpad scroll (native overflow-x:auto)
  - ResizeObserver + scroll event listeners for recalculation
  - Works with Shopify theme editor reloads (re-init on section:load)
  - Graceful degradation: if JS fails, native scroll still works
*/

(function () {
  'use strict';

  /* ----------------------------------------------------------
     KennixCarousel class
     Wraps one scroll track + its prev/next arrow buttons.
  ---------------------------------------------------------- */
  class KennixCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-kx-track]');
      this.btnPrev = root.querySelector('[data-kx-prev]');
      this.btnNext = root.querySelector('[data-kx-next]');

      if (!this.track) return;

      this._onScroll = this._update.bind(this);
      this._onResize = this._update.bind(this);

      /* Mouse drag state */
      this._drag = { active: false, startX: 0, scrollLeft: 0 };

      this._attachArrows();
      this._attachDrag();
      this._attachScroll();
      this._attachResize();
      this._update();
    }

    /* ---- Arrow click handlers ---- */
    _attachArrows() {
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => {
          this.track.scrollBy({ left: -this._scrollAmount(), behavior: 'smooth' });
        });
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => {
          this.track.scrollBy({ left: this._scrollAmount(), behavior: 'smooth' });
        });
      }
    }

    /* ---- Scroll amount = visible container width ---- */
    _scrollAmount() {
      return this.track.clientWidth || 300;
    }

    /* ---- Mouse drag (desktop click-and-drag) ---- */
    _attachDrag() {
      const t = this.track;

      t.addEventListener('mousedown', (e) => {
        this._drag.active = true;
        this._drag.startX = e.pageX - t.offsetLeft;
        this._drag.scrollLeft = t.scrollLeft;
        t.style.cursor = 'grabbing';
        t.style.userSelect = 'none';
      });

      const end = () => {
        this._drag.active = false;
        t.style.cursor = '';
        t.style.userSelect = '';
      };
      t.addEventListener('mouseup', end);
      t.addEventListener('mouseleave', end);

      t.addEventListener('mousemove', (e) => {
        if (!this._drag.active) return;
        e.preventDefault();
        const x = e.pageX - t.offsetLeft;
        const walk = (x - this._drag.startX) * 1.2;
        t.scrollLeft = this._drag.scrollLeft - walk;
      });
    }

    /* ---- Scroll listener to update arrow visibility ---- */
    _attachScroll() {
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
    }

    /* ---- ResizeObserver: recalculate on layout change ---- */
    _attachResize() {
      if (typeof ResizeObserver === 'undefined') return;
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(this.track);
    }

    /* ---- Core update: show/hide arrows based on scroll state ---- */
    _update() {
      const t = this.track;
      /* 2px tolerance for sub-pixel rounding */
      const atStart = t.scrollLeft <= 2;
      const atEnd   = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
      const noOverflow = t.scrollWidth <= t.clientWidth + 2;

      if (this.btnPrev) {
        this.btnPrev.hidden = atStart || noOverflow;
        this.btnPrev.setAttribute('aria-hidden', String(atStart || noOverflow));
      }
      if (this.btnNext) {
        this.btnNext.hidden = atEnd || noOverflow;
        this.btnNext.setAttribute('aria-hidden', String(atEnd || noOverflow));
      }
    }

    destroy() {
      this.track.removeEventListener('scroll', this._onScroll);
      if (this._ro) this._ro.disconnect();
    }
  }

  /* ----------------------------------------------------------
     Init: find all carousel roots on the page
  ---------------------------------------------------------- */
  function initAll() {
    document.querySelectorAll('[data-kx-carousel]').forEach((root) => {
      if (root._kxCarousel) root._kxCarousel.destroy();
      root._kxCarousel = new KennixCarousel(root);
    });
  }

  /* Run on first load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-run when Shopify theme editor reloads a section */
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);
})();
